import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    this.#animateLayoutChange(target.value);
  }

  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;
    if (!grid) return;
    await startViewTransition(() => this.#setLayout(value), ['product-grid']);
    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');
    if (!(targetElement instanceof HTMLInputElement)) return;
    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}

class VariantPills extends HTMLElement {
  connectedCallback() {
    this.moveBeforePrice();

    this.addEventListener('click', (event) => {
      const pill = event.target.closest('.variant-pill');
      if (!pill || pill.disabled) return;

      event.preventDefault();
      event.stopPropagation();

      this.querySelectorAll('.variant-pill').forEach((p) =>
        p.setAttribute('aria-pressed', 'false')
      );
      pill.setAttribute('aria-pressed', 'true');
      this.selectVariant(pill);
    });
  }

  get card() {
    return this.closest('.product-card');
  }

  get priceComponent() {
    return this.card?.querySelector('product-price') ?? null;
  }

  moveBeforePrice() {
    if (this.dataset.moved === 'true') return;
    const price = this.priceComponent;
    if (price && price.parentNode) {
      this.dataset.moved = 'true';
      price.parentNode.insertBefore(this, price);
    }
  }

  selectVariant(pill) {
    const card = this.card;
    if (!card) return;

    // 1. Ár
    const price = this.priceComponent;
    if (price && pill.dataset.priceFormatted) {
      const container = price.querySelector('[ref="priceContainer"]') ?? price;
      container.textContent = pill.dataset.priceFormatted;
    }

    // 2. A rejtett variáns-input frissítése
    if (pill.dataset.variantId) {
      card.querySelectorAll('input[name="id"]').forEach((input) => {
        input.value = pill.dataset.variantId;
        input.removeAttribute('disabled');
      });
    }

    // 3. A quick-add gombot "add" módba állítjuk, hogy ne modalt nyisson
    const quickAdd = card.querySelector('quick-add-component');
    if (quickAdd) {
      quickAdd.setAttribute('data-quick-add-button', 'add');
    }

    // 4. Kártya linkje
    const link = card.querySelector('a.product-card__link');
    if (link && pill.dataset.variantUrl) {
      link.setAttribute('href', pill.dataset.variantUrl);
    }

    // 5. Kép csere
    const img = card.querySelector('.card-gallery img, .product-card__image img, img');
    if (img && pill.dataset.variantImage) {
      img.removeAttribute('srcset');
      img.src = pill.dataset.variantImage;
    }
  }
}

if (!customElements.get('variant-pills')) {
  customElements.define('variant-pills', VariantPills);
}